import type { ImportStatus } from "./types/imports";

// Copy for the import progress banner and the Connected sources line, shared
// so web and mobile say exactly the same thing. Strava gives no reliable walk
// total up front, so fetching is "N so far", never "of about N".

const PROVIDER_LABELS: Record<string, string> = {
  strava: "Strava",
  apple_health: "Apple Health",
  health_connect: "Health Connect",
  komoot: "Komoot",
  garmin: "Garmin",
  gpx: "your GPX files",
};

export function importProviderLabel(provider: string | null): string {
  if (!provider) return "your source";
  return PROVIDER_LABELS[provider] ?? provider;
}

function count(n: number): string {
  return n.toLocaleString("en-GB");
}

function walks(n: number): string {
  return `${count(n)} ${n === 1 ? "walk" : "walks"}`;
}

export interface ImportCopy {
  title: string;
  detail: string;
}

export const IMPORT_FINISHED_COPY: ImportCopy = {
  title: "Import finished",
  detail: "Your record is up to date.",
};

/**
 * The "Import finished" note. When the run that just ended left nothing
 * from that source on the record (ATL-173) it says so and points at
 * Connected sources, where the card explains why, instead of "up to date".
 * A quiet sync with walks already on the record keeps the usual copy.
 */
export function importFinishedCopy(status: ImportStatus | undefined): ImportCopy {
  const run = status?.lastRun;
  if (!run || run.imported > 0 || run.onRecord > 0) return IMPORT_FINISHED_COPY;
  const source = importProviderLabel(run.provider);
  return {
    title: "Import finished",
    detail:
      run.skipped > 0
        ? `Nothing from ${source} could be matched yet. Connected sources explains what was left out.`
        : `${source} didn't share any activities with Atlas. Connected sources explains what to check.`,
  };
}

const FILL_IN =
  "Your collections, trails and achievements fill in as this finishes. Nothing is missed.";

/**
 * Banner copy for an active import. `formatTime` turns the ISO resume time
 * into a local "14:15" (web and mobile format dates their own way).
 */
export function importBannerCopy(
  status: ImportStatus,
  formatTime: (iso: string) => string,
): ImportCopy | null {
  if (!status.active) return null;
  const source = importProviderLabel(status.provider);
  switch (status.phase) {
    case "fetching":
      return {
        title: "Importing your history",
        detail:
          status.fetched > 0
            ? `${walks(status.fetched)} fetched from ${source} so far. ${FILL_IN}`
            : `Fetching your walks from ${source}. ${FILL_IN}`,
      };
    case "waiting": {
      const resume = status.resumeAt
        ? `Paused for ${source}'s rate limit, resumes about ${formatTime(status.resumeAt)}.`
        : `Paused for ${source}'s rate limit.`;
      return {
        title: "Importing your history",
        detail: `${walks(status.fetched)} fetched from ${source} so far. ${resume}`,
      };
    }
    case "matching":
      return {
        title: `Matching your walks: ${count(status.matched)} of ${count(status.matched + status.pending)}`,
        detail: FILL_IN,
      };
    default:
      return null;
  }
}

/** One short line for a source card on Connected sources. */
export function importSourceLine(
  status: ImportStatus,
  formatTime: (iso: string) => string,
): string | null {
  if (!status.active) return null;
  const source = importProviderLabel(status.provider);
  switch (status.phase) {
    case "fetching":
      return `Fetching from ${source}: ${count(status.fetched)} so far`;
    case "waiting":
      return status.resumeAt
        ? `Paused for ${source}'s rate limit, resumes about ${formatTime(status.resumeAt)}`
        : `Paused for ${source}'s rate limit`;
    case "matching":
      return `Matching: ${count(status.matched)} of ${count(status.matched + status.pending)}`;
    default:
      return null;
  }
}
